import React from 'react'
import * as R from 'remeda'

import {
  ItemMultiSelector,
  type ItemMultiSelectorProps,
} from '~/components/elements/ItemMultiSelector'
import { type CustomerAttribute as CustomerAttributeBase } from '~/gql/graphql'

type CustomerAttribute = App.PickGraphQL<CustomerAttributeBase, 'id' | 'name'>

export type CustomerAttributeMultiSelectorProps<
  TCustomerAttribute extends CustomerAttribute,
> = Omit<
  ItemMultiSelectorProps,
  'data' | 'dataItemKey' | 'onChange' | 'textField' | 'value'
> & {
  onChange?: (customerAttributeId: TCustomerAttribute['id'][]) => void
  customerAttributes: TCustomerAttribute[]
  value: TCustomerAttribute['id'][]
}

export const CustomerAttributeMultiSelector = React.forwardRef(
  <TCustomerAttribute extends CustomerAttribute>(
    {
      customerAttributes,
      onChange,
      value: valueProp,
      ...rest
    }: CustomerAttributeMultiSelectorProps<TCustomerAttribute>,
    ref: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const value = React.useMemo(
      () => customerAttributes.filter(({ id }) => valueProp.includes(id)),
      [customerAttributes, valueProp],
    )

    const handleChange = React.useCallback<
      EH<ItemMultiSelectorProps, 'onChange'>
    >(
      (value: TCustomerAttribute[]) => {
        onChange?.(value.map(R.prop('id')))
      },
      [onChange],
    )

    return (
      <ItemMultiSelector
        ref={ref}
        data={customerAttributes}
        dataItemKey="id"
        textField="name"
        value={value}
        onChange={handleChange}
        {...rest}
      />
    )
  },
)
